import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fadeIn, fadeInUp } from '@/utils/animations';
import {
  serviceTypes,
  materialTypes,
  finishTypes,
  complexityLevels,
  urgencyLevels,
  installationTypes,
  locationFactors,
  calculateEstimate
} from '@/data/estimationData';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { FaDollarSign, FaRuler, FaTools, FaCheckCircle, FaTruck, FaHourglassHalf } from 'react-icons/fa';

interface EstimationCalculatorProps {
  onContactClick: () => void;
}

const EstimationCalculator = ({ onContactClick }: EstimationCalculatorProps) => {
  // State for form values
  const [serviceType, setServiceType] = useState<string>('');
  const [material, setMaterial] = useState<string>('');
  const [finish, setFinish] = useState<string>('');
  const [complexity, setComplexity] = useState<string>('');
  const [urgency, setUrgency] = useState<string>('');
  const [installation, setInstallation] = useState<string>('');
  const [size, setSize] = useState<number>(10); // Default size (feet or hours)
  const [additionalDetails, setAdditionalDetails] = useState<string>('');

  // State for estimate result
  const [estimate, setEstimate] = useState<{
    minEstimate: number;
    maxEstimate: number;
    error: string | null;
  } | null>(null);

  // State for form completion
  const [formComplete, setFormComplete] = useState<boolean>(false);

  // Selected service details
  const [selectedService, setSelectedService] = useState<any>(null);
  const [sizeLabel, setSizeLabel] = useState<string>('Size (feet)');
  const [sizeDescription, setSizeDescription] = useState<string>('');

  // Handle service type change to update related fields
  useEffect(() => {
    if (serviceType) {
      const service = serviceTypes.find(s => s.id === serviceType);
      setSelectedService(service);

      if (service) {
        if ('basePricePerFoot' in service) {
          setSizeLabel('Size (feet)');
          setSizeDescription('Approximate length in feet');
        } else if ('basePricePerHour' in service) {
          setSizeLabel('Project Scale');
          setSizeDescription('Estimated project scope (higher = larger/more complex)');
        }
      }
    }
  }, [serviceType]);

  // Check if form is complete enough to calculate an estimate
  useEffect(() => {
    setFormComplete(
      !!serviceType && 
      !!material && 
      !!finish && 
      !!complexity && 
      !!urgency && 
      !!installation
    );
  }, [serviceType, material, finish, complexity, urgency, installation]);

  // Calculate estimate when form is complete and user changes values
  useEffect(() => {
    if (formComplete) {
      const result = calculateEstimate({
        serviceType,
        material,
        finish,
        complexity,
        urgency,
        installation,
        size,
        additionalDetails
      });
      setEstimate(result);
    }
  }, [formComplete, serviceType, material, finish, complexity, urgency, installation, size, additionalDetails]);

  const handleGetPersonalizedQuote = () => {
    // Scroll to contact section
    onContactClick();
  };

  return (
    <section id="estimation" className="py-16 px-4 bg-gray-50">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Project Estimation Tool</h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Get a rough estimate for your welding project. Please note this is just an approximation, 
            and we'll provide a detailed quote after consultation.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Form Column */}
          <motion.div 
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:col-span-7"
          >
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Project Details</CardTitle>
                <CardDescription>Fill in the details below for a cost estimate</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Service Type */}
                <div className="space-y-2">
                  <Label htmlFor="service-type">Service Type</Label>
                  <Select value={serviceType} onValueChange={setServiceType}>
                    <SelectTrigger id="service-type">
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedService && (
                    <p className="text-sm text-gray-500">{selectedService.description}</p>
                  )}
                </div>

                {/* Material */}
                <div className="space-y-2">
                  <Label htmlFor="material">Material</Label>
                  <Select value={material} onValueChange={setMaterial}>
                    <SelectTrigger id="material">
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent>
                      {materialTypes.map((material) => (
                        <SelectItem key={material.id} value={material.id}>
                          {material.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {material && (
                    <p className="text-sm text-gray-500">
                      {materialTypes.find(m => m.id === material)?.description}
                    </p>
                  )}
                </div>

                {/* Finish */}
                <div className="space-y-2">
                  <Label htmlFor="finish">Finish</Label>
                  <Select value={finish} onValueChange={setFinish}>
                    <SelectTrigger id="finish">
                      <SelectValue placeholder="Select finish" />
                    </SelectTrigger>
                    <SelectContent>
                      {finishTypes.map((finish) => (
                        <SelectItem key={finish.id} value={finish.id}>
                          {finish.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {finish && (
                    <p className="text-sm text-gray-500">
                      {finishTypes.find(f => f.id === finish)?.description}
                    </p>
                  )}
                </div>

                {/* Complexity */}
                <div className="space-y-2">
                  <Label htmlFor="complexity">Design Complexity</Label>
                  <Select value={complexity} onValueChange={setComplexity}>
                    <SelectTrigger id="complexity">
                      <SelectValue placeholder="Select complexity" />
                    </SelectTrigger>
                    <SelectContent>
                      {complexityLevels.map((level) => (
                        <SelectItem key={level.id} value={level.id}>
                          {level.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {complexity && (
                    <p className="text-sm text-gray-500">
                      {complexityLevels.find(c => c.id === complexity)?.description}
                    </p>
                  )}
                </div>

                {/* Size */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="size">{sizeLabel}</Label>
                    <span className="text-sm font-medium">{size}</span>
                  </div>
                  <Slider
                    id="size"
                    min={1}
                    max={selectedService && 'basePricePerHour' in selectedService ? 30 : 100}
                    step={1}
                    value={[size]}
                    onValueChange={(value) => setSize(value[0])}
                  />
                  <p className="text-sm text-gray-500">{sizeDescription}</p>
                </div>

                {/* Urgency */}
                <div className="space-y-2">
                  <Label htmlFor="urgency">Timeframe</Label>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger id="urgency">
                      <SelectValue placeholder="Select timeframe" />
                    </SelectTrigger>
                    <SelectContent>
                      {urgencyLevels.map((level) => (
                        <SelectItem key={level.id} value={level.id}>
                          {level.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {urgency && (
                    <p className="text-sm text-gray-500">
                      {urgencyLevels.find(u => u.id === urgency)?.timeDescription}
                    </p>
                  )}
                </div>

                {/* Installation */}
                <div className="space-y-2">
                  <Label htmlFor="installation">Installation</Label>
                  <Select value={installation} onValueChange={setInstallation}>
                    <SelectTrigger id="installation">
                      <SelectValue placeholder="Select installation type" />
                    </SelectTrigger>
                    <SelectContent>
                      {installationTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {installation && (
                    <p className="text-sm text-gray-500">
                      {installationTypes.find(i => i.id === installation)?.description}
                    </p>
                  )}
                </div>

                {/* Additional Details */}
                <div className="space-y-2">
                  <Label htmlFor="additional-details">Additional Details (Optional)</Label>
                  <Textarea
                    id="additional-details"
                    placeholder="Please provide any additional details about your project"
                    value={additionalDetails}
                    onChange={(e) => setAdditionalDetails(e.target.value)}
                    className="min-h-[100px]"
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Results Column */}
          <motion.div 
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <div className="sticky top-24">
              <Card className={cn(
                "shadow-lg transition-all duration-300",
                formComplete ? "border-green-400" : "border-gray-200"
              )}>
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <FaDollarSign className="text-green-600" />
                    Estimate Preview
                  </CardTitle>
                  <CardDescription>
                    {formComplete 
                      ? "Based on your selections" 
                      : "Please fill in the project details"
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {formComplete && estimate ? (
                    <>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="text-center">
                          <h3 className="text-lg font-medium text-gray-700 mb-2">Estimated Price Range</h3>
                          <p className="text-3xl font-bold text-green-600">
                            ${estimate.minEstimate.toLocaleString()} - ${estimate.maxEstimate.toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            This is a rough estimate based on the information provided
                          </p>
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <h3 className="font-medium">Selected Options:</h3>

                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <FaTools className="text-gray-500" />
                            <span className="text-gray-700">Service:</span>
                          </div>
                          <div className="font-medium">
                            {serviceTypes.find(s => s.id === serviceType)?.name}
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-gray-700">Material:</span>
                          </div>
                          <div className="font-medium">
                            {materialTypes.find(m => m.id === material)?.name}
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-gray-700">Finish:</span>
                          </div>
                          <div className="font-medium">
                            {finishTypes.find(f => f.id === finish)?.name}
                          </div>

                          <div className="flex items-center gap-2">
                            <FaRuler className="text-gray-500" />
                            <span className="text-gray-700">{sizeLabel}:</span>
                          </div>
                          <div className="font-medium">
                            {size} {serviceType.includes('railing') || serviceType.includes('structural') ? 'feet' : ''}
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-gray-700">Complexity:</span>
                          </div>
                          <div className="font-medium">
                            {complexityLevels.find(c => c.id === complexity)?.name}
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-gray-700">Timeframe:</span>
                          </div>
                          <div className="font-medium">
                            {urgencyLevels.find(u => u.id === urgency)?.name}
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-700">
                          For a personalized quote, please contact us with your project details. We'll provide an accurate 
                          estimate based on your specific requirements.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                      <div className="bg-gray-100 p-4 rounded-full mb-4">
                        <FaDollarSign className="text-3xl text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium">Calculate Your Estimate</h3>
                      <p className="text-gray-500 mt-2 max-w-xs">
                        Fill in the project details on the left to see an estimated price range for your project
                      </p>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="flex flex-col space-y-3">
                  <Button 
                    onClick={handleGetPersonalizedQuote} 
                    className="w-full bg-black hover:bg-gray-800 text-white py-2"
                  >
                    Get a Personalized Quote
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    This estimate is for reference only. Final pricing may vary based on detailed requirements,
                    site conditions, and material availability.
                  </p>
                </CardFooter>
              </Card>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default EstimationCalculator;